if (!customElements.get('product-file-upload')) {
  class ProductFileUpload extends HTMLElement {
    constructor() {
      super();
      this.dropzone = this.querySelector('.product__file-upload__dropzone');
      this.input = this.querySelector('.product__file-upload__input');
      this.previewsContainer = this.querySelector('.product__file-upload__previews');
      this.actionsContainer = this.querySelector('.product__file-upload__actions');
      this.clearAllButton = this.querySelector('.product__file-upload__clear-all');
      this.error = this.querySelector('.product__file-upload__error');
      this.errorMessage = this.querySelector('.product__file-upload__error-message');
      this.maxSize = parseInt(this.querySelector('.product__file-upload__container').dataset.maxSize) || 20; // 20Mo par défaut
      this.maxFiles = parseInt(this.querySelector('.product__file-upload__container').dataset.maxFiles) || 4; // 4 fichiers par défaut

      // Tableau pour stocker les fichiers
      this.files = [];
      
      // Stocker les attributs originaux de l'input
      this.originalInputName = this.input.getAttribute('name');
      this.originalFormId = this.input.getAttribute('form');

      // Éléments pour l'animation de chargement
      this.loadingOverlay = null;
      this.loadingTextTimeout = null;

      this.init();
    }

    init() {
      // Créer l'overlay de chargement
      this.createLoadingOverlay();
      // Événement de clic sur la zone de drop
      this.dropzone.addEventListener('click', () => {
        this.input.click();
      });

      // Événements de drag and drop
      this.dropzone.addEventListener('dragover', (e) => {
        e.preventDefault();
        this.dropzone.classList.add('dragover');
      });

      this.dropzone.addEventListener('dragleave', () => {
        this.dropzone.classList.remove('dragover');
      });

      this.dropzone.addEventListener('drop', (e) => {
        e.preventDefault();
        this.dropzone.classList.remove('dragover');

        if (e.dataTransfer.files.length) {
          this.handleFiles(e.dataTransfer.files);
        }
      });

      // Événement de changement de fichier via l'input
      this.input.addEventListener('change', (event) => {
        // Ne traiter l'événement que s'il ne provient pas d'une action interne
        if (!event.detail || (event.detail.reason !== 'file_removed' && event.detail.reason !== 'file_updated')) {
          if (this.input.files.length) {
            this.handleFiles(this.input.files);
          }
        }
      });

      // Événement de suppression de tous les fichiers
      this.clearAllButton.addEventListener('click', () => {
        this.clearAllFiles();
      });

      // Événement de clic sur le bouton d'ajout au panier
      this.initAddToCartListener();
    }

    /**
     * Initialise l'écouteur sur le bouton d'ajout au panier
     */
    initAddToCartListener() {
      // Chercher le bouton par son ID ou sa classe
      this.addToCartButton = document.querySelector('[id^="ProductSubmitButton"], .product-form__submit');
      
      if (this.addToCartButton) {
        this.addToCartButton.addEventListener('click', (e) => {
          // Vérifier qu'il y a des fichiers à uploader
          if (this.files.length > 0) {
            this.showLoading();
          }
        });

        // Observer les changements sur le bouton (loading state)
        this.observeButtonState();
      }
    }

    /**
     * Observe le bouton pour détecter quand l'ajout au panier est terminé
     */
    observeButtonState() {
      let wasLoading = false;
      
      const observer = new MutationObserver((mutations) => {
        const isLoading = this.addToCartButton.classList.contains('loading') || 
                          this.addToCartButton.getAttribute('aria-busy') === 'true' ||
                          this.addToCartButton.disabled;
        
        // Si le bouton était en loading et ne l'est plus = ajout terminé
        if (wasLoading && !isLoading) {
          this.hideLoading();
        }
        
        wasLoading = isLoading;
      });

      observer.observe(this.addToCartButton, {
        attributes: true,
        attributeFilter: ['class', 'disabled', 'aria-busy', 'aria-disabled']
      });
    }

    async handleFiles(fileList) {
      // Cacher les erreurs précédentes
      this.hideError();

      // Convertir FileList en Array pour pouvoir itérer dessus
      let filesArray = Array.from(fileList);

      // Vérifier la limite de fichiers
      const remainingSlots = this.maxFiles - this.files.length;
      if (remainingSlots <= 0) {
        this.showError(`Vous ne pouvez pas ajouter plus de ${this.maxFiles} images`);
        return;
      }
      if (filesArray.length > remainingSlots) {
        this.showError(`Vous ne pouvez ajouter que ${remainingSlots} image(s) supplémentaire(s)`);
        filesArray = filesArray.slice(0, remainingSlots); // Garder seulement les premiers fichiers autorisés
      }

      // Traiter tous les fichiers EN PARALLÈLE pour de meilleures performances
      const processedFiles = await Promise.all(
        filesArray.map(async (originalFile) => {
          // Vérifier si c'est une image
          if (!originalFile.type.startsWith('image/')) {
            this.showError('Le fichier doit être une image (JPG, PNG, GIF, etc.)');
            return null;
          }

          // Vérifier la taille du fichier (en Mo)
          const fileSizeMB = originalFile.size / (1024 * 1024);
          if (fileSizeMB > this.maxSize) {
            this.showError(`La taille du fichier ne doit pas dépasser ${this.maxSize}Mo`);
            return null;
          }

          // Compresser l'image (max 1500px, max 2Mo)
          let file;
          try {
            file = await this.compressImage(originalFile, 800, 0.8);
          } catch (e) {
            console.error('Failed to compress image:', originalFile.name, e);
            file = originalFile; // Utiliser l'original en cas d'erreur
          }

          // iOS fix: créer le DataTransfer IMMÉDIATEMENT après compression,
          // avant toute modification du DOM ou de l'input original
          try {
            const dt = new DataTransfer();
            dt.items.add(file);
            file._dataTransfer = dt;
          } catch (e) {
            console.error('Failed to create DataTransfer for file:', file.name);
            return null;
          }

          return file;
        })
      );

      // Filtrer les fichiers null (invalides) et ajouter les valides
      processedFiles.filter(Boolean).forEach((file) => {
        this.files.push(file);
        this.createPreview(file);
      });

      // Mettre à jour les inputs cachés après avoir traité tous les fichiers
      this.updateInputFiles();

      // Mettre à jour l'affichage
      this.updateDisplay();
    }

    /**
     * Compresse une image en réduisant ses dimensions et sa qualité
     * @param {File} file - Le fichier image à compresser
     * @param {number} maxDimension - Dimension maximale (largeur ou hauteur) en pixels
     * @param {number} maxSizeMB - Taille maximale en Mo
     * @returns {Promise<File>} - Le fichier compressé
     */
    compressImage(file, maxDimension = 1500, maxSizeMB = 2) { 
      return new Promise((resolve, reject) => {
        const img = new Image();
        
        img.onload = () => {
          URL.revokeObjectURL(img.src); // Libérer la mémoire
          
          let { width, height } = img;
          
          // Réduire les dimensions si nécessaire
          if (width > maxDimension || height > maxDimension) {
            const ratio = Math.min(maxDimension / width, maxDimension / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Compression itérative jusqu'à atteindre la taille cible
          let quality = 0.9;
          const maxBytes = maxSizeMB * 1024 * 1024;
          
          const tryCompress = () => {
            canvas.toBlob((blob) => {
              if (!blob) {
                reject(new Error('Failed to create blob'));
                return;
              }
              
              if (blob.size > maxBytes && quality > 0.1) {
                quality -= 0.1;
                tryCompress();
              } else {
                // Créer un nouveau File avec le nom original
                const compressedFile = new File([blob], file.name, { 
                  type: 'image/jpeg',
                  lastModified: Date.now()
                });
                resolve(compressedFile);
              }
            }, 'image/jpeg', quality);
          };
          
          tryCompress();
        };
        
        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };
        
        img.src = URL.createObjectURL(file);
      });
    }

    createPreview(file) {
      const previewId = `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const previewElement = document.createElement('div');
      previewElement.className = 'product__file-upload__preview';
      previewElement.dataset.id = previewId;

      // Créer un objet URL pour l'aperçu de l'image
      const objectUrl = URL.createObjectURL(file);

      previewElement.innerHTML = `
      <div class="product__file-upload__preview-image-container">
        <img src="${objectUrl}" alt="${file.name}" width="80" height="80" class="product__file-upload__preview-image">
        <button type="button" class="product__file-upload__remove-single" data-id="${previewId}">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    `;

      // Ajouter l'événement de suppression
      const removeButton = previewElement.querySelector('.product__file-upload__remove-single');
      removeButton.addEventListener('click', () => {
        this.removeFile(previewId);
      });

      // Ajouter l'aperçu au conteneur
      this.previewsContainer.appendChild(previewElement);

      // Stocker l'ID et l'URL de l'objet avec le fichier pour pouvoir les nettoyer plus tard
      file.previewId = previewId;
      file.objectUrl = objectUrl;
    }

    removeFile(previewId) {
      // Trouver l'index du fichier dans le tableau
      const fileIndex = this.files.findIndex((file) => file.previewId === previewId);

      if (fileIndex !== -1) {
        // Récupérer le fichier
        const file = this.files[fileIndex];

        // Supprimer l'aperçu du DOM
        const previewElement = this.previewsContainer.querySelector(`[data-id="${previewId}"]`);
        if (previewElement) {
          previewElement.remove();
        }

        // Révoquer l'URL de l'objet pour libérer la mémoire
        URL.revokeObjectURL(file.objectUrl);

        // Supprimer le fichier du tableau
        this.files.splice(fileIndex, 1);

        // Mettre à jour l'input avec les fichiers actuels
        this.updateInputFiles();

        // Mettre à jour l'affichage
        this.updateDisplay();
      }
    }

    clearAllFiles() {
      // Supprimer tous les aperçus
      this.previewsContainer.innerHTML = '';

      // Révoquer toutes les URLs d'objets
      this.files.forEach((file) => {
        if (file.objectUrl) {
          URL.revokeObjectURL(file.objectUrl);
        }
      });

      // Vider le tableau de fichiers
      this.files = [];

      // Mettre à jour l'input avec les fichiers actuels (vide)
      this.updateInputFiles();

      // Mettre à jour l'affichage
      this.updateDisplay();
    }

    updateInputFiles() {
      // Supprimer tous les inputs cachés existants
      this.clearHiddenInputs();

      if (this.files.length > 0) {
        // Vider les attributs de l'input original pour qu'il ne soit pas envoyé
        this.input.removeAttribute('name');
        if (this.originalFormId) {
          this.input.removeAttribute('form');
        }

        // Créer un input caché pour chaque fichier avec un nom numéroté

        this.files.forEach((file, index) => {
          this.createHiddenInput(file, index + 1);
        });
      } else {
        // Restaurer les attributs originaux s'il n'y a plus de fichiers
        this.input.setAttribute('name', this.originalInputName);
        if (this.originalFormId) {
          this.input.setAttribute('form', this.originalFormId);
        }
      }

      // Déclencher un événement change pour que le formulaire sache que les fichiers ont changé
      const event = new CustomEvent('change', {
        bubbles: true,
        detail: { reason: 'file_updated' },
      });
      this.input.dispatchEvent(event);
    }

    /**
     * Crée un input caché pour un fichier spécifique
     * @param {File} file - Le fichier à traiter
     * @param {number} index - L'index du fichier (1-based)
     */
    createHiddenInput(file, index) {
      const hiddenInput = document.createElement('input');
      hiddenInput.type = 'file';
      hiddenInput.className = 'product__file-upload__hidden-input';
      hiddenInput.style.display = 'none';
      
      // Utiliser l'attribut form stocké
      if (this.originalFormId) {
        hiddenInput.setAttribute('form', this.originalFormId);
      }
      
      // Créer un nom numéroté basé sur le nom original de l'input
      const baseName = this.originalInputName.replace(/properties\[([^\]]+)\]/, '$1');
      hiddenInput.name = `properties[${baseName}-${index}]`;

      // iOS fix: utiliser le DataTransfer pré-créé si disponible
      if (file._dataTransfer) {
        hiddenInput.files = file._dataTransfer.files;
      } else {
        // Fallback pour les fichiers sans DataTransfer pré-créé
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        hiddenInput.files = dataTransfer.files;
      }

      // Ajouter l'input au DOM
      this.appendChild(hiddenInput);
    }

    /**
     * Supprime tous les inputs cachés créés
     */
    clearHiddenInputs() {
      const hiddenInputs = this.querySelectorAll('.product__file-upload__hidden-input');
      hiddenInputs.forEach(input => input.remove());
    }

    updateDisplay() {
      // Afficher ou masquer la zone de drop en fonction de la présence de fichiers
      if (this.files.length > 0) {
        this.actionsContainer.hidden = false;
      } else {
        this.actionsContainer.hidden = true;
      }
    }

    showError(message) {
      this.errorMessage.textContent = message;
      this.error.hidden = false;
    }

    hideError() {
      this.error.hidden = true;
      this.errorMessage.textContent = '';
    }

    /**
     * Crée l'élément de chargement (positionné au-dessus du bouton)
     */
    createLoadingOverlay() {
      this.loadingOverlay = document.createElement('div');
      this.loadingOverlay.className = 'product__file-upload__loading';
      this.loadingOverlay.innerHTML = `
        <div class="product__file-upload__loading-content">
          <div class="product__file-upload__loading-spinner"></div>
          <p class="product__file-upload__loading-text"></p>
        </div>
      `;
      
      // Styles inline pour le conteneur de loading - ABSOLUTE pour éviter layout shift
      Object.assign(this.loadingOverlay.style, {
        position: 'absolute',
        left: '0',
        right: '0',
        bottom: '100%',
        marginBottom: '0.5rem',
        display: 'none',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0.75rem 1rem',
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        borderRadius: '8px',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.1)',
        zIndex: '10'
      });

      const content = this.loadingOverlay.querySelector('.product__file-upload__loading-content');
      Object.assign(content.style, {
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem'
      });

      const spinner = this.loadingOverlay.querySelector('.product__file-upload__loading-spinner');
      Object.assign(spinner.style, {
        width: '18px',
        height: '18px',
        border: '2px solid #e0e0e0',
        borderTopColor: '#333',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        flexShrink: '0'
      });

      const text = this.loadingOverlay.querySelector('.product__file-upload__loading-text');
      Object.assign(text.style, {
        margin: '0',
        fontSize: '1.1rem',
        color: '#2e7d32',
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontWeight: '600',
        whiteSpace: 'nowrap'
      });

      // Ajouter l'animation de rotation
      if (!document.querySelector('#file-upload-spinner-style')) {
        const style = document.createElement('style');
        style.id = 'file-upload-spinner-style';
        style.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
        document.head.appendChild(style);
      }
    }

    /**
     * Affiche le loading au-dessus du bouton avec animation
     */
    showLoading() {
      if (!this.addToCartButton) return;

      const textElement = this.loadingOverlay.querySelector('.product__file-upload__loading-text');
      
      // S'assurer que le bouton a un parent positionné relative
      const buttonWrapper = this.addToCartButton.parentNode;
      buttonWrapper.style.position = 'relative';
      
      // Ajouter l'overlay dans le wrapper du bouton
      buttonWrapper.appendChild(this.loadingOverlay);
      
      // Afficher l'overlay
      this.loadingOverlay.style.display = 'flex';
      
      // Animation d'entrée de l'overlay
      this.loadingOverlay.animate([
        { opacity: 0, transform: 'translateY(10px)' },
        { opacity: 1, transform: 'translateY(0)' }
      ], { duration: 300, easing: 'ease-out', fill: 'forwards' });

      // Premier texte avec animation
      this.animateTextIn(textElement, 'Vos photos sont en cours de téléchargement');

      // Second texte après 5 secondes
      this.loadingTextTimeout = setTimeout(() => {
        this.animateTextOut(textElement).then(() => {
          this.animateTextIn(textElement, 'Encore trente petites secondes');
        });
      }, 5000);
    }

    /**
     * Cache le loading avec animation
     */
    async hideLoading() {
      // Annuler le timeout du second texte
      if (this.loadingTextTimeout) {
        clearTimeout(this.loadingTextTimeout);
        this.loadingTextTimeout = null;
      }

      // Vérifier que l'overlay est visible
      if (this.loadingOverlay.style.display === 'none') return;

      // Animation de sortie
      const animation = this.loadingOverlay.animate([
        { opacity: 1, transform: 'translateY(0)' },
        { opacity: 0, transform: 'translateY(-10px)' }
      ], { duration: 300, easing: 'ease-in', fill: 'forwards' });

      await animation.finished;
      this.loadingOverlay.style.display = 'none';
      
      // Retirer du DOM
      if (this.loadingOverlay.parentNode) {
        this.loadingOverlay.parentNode.removeChild(this.loadingOverlay);
      }
    }

    /**
     * Anime l'entrée d'un texte (fade in + scale)
     */
    animateTextIn(element, text) {
      element.textContent = text;
      element.animate([
        { opacity: 0, transform: 'scaleY(0.1) translateY(10px)' },
        { opacity: 1, transform: 'scaleY(1) translateY(0)' }
      ], { duration: 400, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)', fill: 'forwards' });
    }

    /**
     * Anime la sortie d'un texte (fade out + scale)
     */
    animateTextOut(element) {
      return new Promise((resolve) => {
        const animation = element.animate([
          { opacity: 1, transform: 'scaleY(1) translateY(0)' },
          { opacity: 0, transform: 'scaleY(0.1) translateY(-10px)' }
        ], { duration: 300, easing: 'ease-in', fill: 'forwards' });
        
        animation.onfinish = resolve;
      });
    }
  }

  customElements.define('product-file-upload', ProductFileUpload);
}
